(() => {
  class BulkBuySaveWidget {
    constructor(root) {
      try {
        this.root = root;
        this.body = root?.querySelector('[data-bulk-buy-body]');
        
        if (!this.body) {
          return;
        }
        
        this.productId = Number(root.dataset.productId || 0);
        this.variantId = Number(root.dataset.variantId || 0);
        this.basePriceCents = Number(root.dataset.basePrice || 0);
        this.variantIds = this.parseVariantIds();
        this.isInitialized = false;

        this.init();
      } catch (error) {
        this.hideWidget();
      }
    }

    async init() {
      if (!this.body) return;
      
      try {
        await this.waitForRegiosApi();

        const tiers = await this.getBulkBuyTiers();
        
        if (!Array.isArray(tiers) || !tiers.length) {
          this.hideWidget();
          return;
        }

        this.root.style.display = '';
        this.render(tiers);
        this.bindVariantChange();
        this.isInitialized = true;
      } catch (error) {
        this.hideWidget();
      }
    }

    waitForRegiosApi() {
      return new Promise((resolve, reject) => {
        try {
          if (window.RegiosDOPP?.api?.v0) {
            resolve(window.RegiosDOPP.api.v0);
            return;
          }

          const timeout = setTimeout(() => {
            window.removeEventListener('regios-dopp:api-initialized', onInit);
            reject(new Error('RegiosDOPP API timeout'));
          }, 5000);

          const onInit = (event) => {
            try {
              clearTimeout(timeout);
              window.removeEventListener('regios-dopp:api-initialized', onInit);

              if (event?.detail?.api) {
                resolve(event.detail.api);
              } else if (window.RegiosDOPP?.api?.v0) {
                resolve(window.RegiosDOPP.api.v0);
              } else {
                reject(new Error('RegiosDOPP API missing'));
              }
            } catch (err) {
              reject(err);
            }
          };

          window.addEventListener('regios-dopp:api-initialized', onInit);
        } catch (error) {
          reject(error);
        }
      });
    }

    parseVariantIds() {
      try {
        const el = this.root.querySelector('[data-bulk-buy-variant-ids]');
        const ids = JSON.parse(el?.textContent || '[]');
        return Array.isArray(ids) ? ids.map(Number).filter(Boolean) : [];
      } catch (error) {
        return [];
      }
    }

    async getBulkBuyTiers() {
      try {
        const api = window.RegiosDOPP?.api?.v0;

        if (!api) {
          return [];
        }

        const calcForQty = async (qty, variantId) => {
          try {
            if (variantId === this.variantId && api.productPage?.calculateDiscountedPrices) {
              const result = await api.productPage.calculateDiscountedPrices({
                variantId,
                quantity: qty,
                cartLines: []
              });
              return this.normalizeTierResult(result, qty);
            }

            if (api.calculateDiscountedPrices) {
              const result = await api.calculateDiscountedPrices({
                productId: this.productId,
                variantId,
                regularPriceInCents: this.basePriceCents,
                quantity: qty,
                cartLines: []
              });
              return this.normalizeTierResult(result, qty);
            }

            return null;
          } catch (err) {
            return null;
          }
        };

        const quantities = Array.from({ length: 19 }, (_, i) => i + 2);

        const fetchTiers = async (variantId) => {
          const [baseline, ...allResults] = await Promise.all(
            [1, ...quantities].map((qty) => calcForQty(qty, variantId).catch(() => null))
          );

          const baselineUnitPrice = baseline ? baseline.newPriceCents : null;
          const beatsBaseline = (tier) =>
            baselineUnitPrice === null ||
            baselineUnitPrice - tier.newPriceCents / tier.qty > 0.001;

          return { baseline, tiers: allResults.filter(Boolean).filter(beatsBaseline) };
        };

        let { baseline, tiers: validTiers } = await fetchTiers(this.variantId);

        if (baseline && !validTiers.length) {
          const MAX_SIBLINGS = 3;
          const siblings = this.variantIds
            .filter((id) => id !== this.variantId)
            .slice(0, MAX_SIBLINGS);

          for (const siblingId of siblings) {
            ({ tiers: validTiers } = await fetchTiers(siblingId));
            if (validTiers.length) break;
          }
        }

        const uniqueDiscounts = new Map();
        
        validTiers.forEach((tier) => {
          try {
            const discount = tier.discount;
            
            if (discount <= 0) return;
            
            if (!uniqueDiscounts.has(discount) || tier.qty < uniqueDiscounts.get(discount).qty) {
              uniqueDiscounts.set(discount, tier);
            }
          } catch (err) {
            // Skip invalid tier
          }
        });

        return Array.from(uniqueDiscounts.values()).sort((a, b) => a.qty - b.qty);
      } catch (error) {
        return [];
      }
    }

    normalizeTierResult(result, qty) {
      try {
        if (!result?.salePrice || !result?.regularPrice) {
          return null;
        }

        const saleAmount = Number(result.salePrice.amount || 0);
        const regularAmount = Number(result.regularPrice.amount || 0);

        if (!regularAmount || saleAmount >= regularAmount) {
          return null;
        }

        const discountPercent = Math.round((1 - saleAmount / regularAmount) * 100);

        return {
          qty,
          discount: discountPercent,
          oldPriceCents: regularAmount,
          newPriceCents: saleAmount,
          saleFormatted: result.salePrice.formatted,
          regularFormatted: result.regularPrice.formatted
        };
      } catch (error) {
        return null;
      }
    }

    formatMoney(cents) {
      try {
        if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
          const moneyFormat =
            window.theme?.moneyFormat ||
            window.Shopify.money_format ||
            '${{amount}}';

          return window.Shopify.formatMoney(cents, moneyFormat);
        } else if(window?.theme?.settings?.moneyWithCurrencyFormat) {
            return window.theme.settings.moneyWithCurrencyFormat.replace(/\{\{\s*(\w+)\s*\}\}/, (cents / 100).toFixed(2))
        }

        return `$${(cents / 100).toFixed(2)}`;
      } catch (error) {
        return `$${(cents / 100).toFixed(2)}`;
      }
    }

    render(tiers) {
      try {
        if (!this.body || !Array.isArray(tiers) || !tiers.length) {
          this.hideWidget();
          return;
        }

        const rowsHtml = tiers.map((tier) => {
          try {
            const oldPricePerUnitCents = (tier.oldPriceCents * 100) / tier.qty;
            const newPricePerUnitCents = (tier.newPriceCents * 100) / tier.qty;
            
            const oldPricePerUnit = this.formatMoney(oldPricePerUnitCents);
            const newPricePerUnit = this.formatMoney(newPricePerUnitCents);

            return `
              <div class="bulk-buy-widget__row">
                <div class="bulk-buy-widget__qty">Buy ${this.escapeHtml(tier.qty)}</div>
                <div class="bulk-buy-widget__save">Save ${this.escapeHtml(tier.discount)}%</div>
                <div class="bulk-buy-widget__price-wrap">
                  <span class="bulk-buy-widget__old-price">${this.escapeHtml(oldPricePerUnit)}</span>
                  <span class="bulk-buy-widget__new-price">${this.escapeHtml(newPricePerUnit)}</span>
                </div>
              </div>
            `;
          } catch (err) {
            return '';
          }
        }).filter(Boolean).join('');

        this.body.innerHTML = rowsHtml;
      } catch (error) {
        this.hideWidget();
      }
    }

    hideWidget() {
      try {
        if (this.root) {
          this.root.style.display = 'none';
        }
      } catch (error) {
        // Silently fail
      }
    }

    bindVariantChange() {
      try {
        const handler = async (event) => {
          try {
            const variant = event.detail?.variant;
            if (!variant || !this.isInitialized) return;

            this.variantId = Number(variant.id || this.variantId);
            this.basePriceCents = Number(variant.price || this.basePriceCents);

            if (window.RegiosDOPP) {
              try {
                window.dispatchEvent(new CustomEvent('regios-dopp:variant-change', {
                  detail: {
                    selectedVariant: {
                      id: this.variantId,
                      price: this.basePriceCents
                    },
                    quantity: 1
                  }
                }));
              } catch (err) {
                // Silently fail if Regios event fails
              }
            }

            await this.refresh();
          } catch (error) {
            // Silently fail on variant change error
          }
        };

        document.addEventListener('variant:change', handler);
      } catch (error) {
        // Silently fail if event binding fails
      }
    }

    async refresh() {
      try {
        if (!this.isInitialized) return;
        
        const tiers = await this.getBulkBuyTiers();

        if (!Array.isArray(tiers) || !tiers.length) {
          this.hideWidget();
          return;
        }

        this.root.style.display = '';
        this.render(tiers);
      } catch (error) {
        this.hideWidget();
      }
    }

    escapeHtml(value) {
      try {
        return String(value)
          .replaceAll('&', '&amp;')
          .replaceAll('<', '&lt;')
          .replaceAll('>', '&gt;')
          .replaceAll('"', '&quot;')
          .replaceAll("'", '&#039;');
      } catch (error) {
        return '';
      }
    }
  }

  function initBulkBuyWidgets() {
    try {
      const widgets = document.querySelectorAll('.bulk-buy-widget');
      if (!widgets || widgets.length === 0) return;
      
      widgets.forEach((root) => {
        try {
          if (root.dataset.initialized === 'true') return;
          root.dataset.initialized = 'true';
          new BulkBuySaveWidget(root);
        } catch (error) {
          // Silently fail for individual widget
        }
      });
    } catch (error) {
      // Silently fail if initialization fails
    }
  }

  try {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initBulkBuyWidgets);
    } else {
      initBulkBuyWidgets();
    }

    document.addEventListener('shopify:section:load', initBulkBuyWidgets);
  } catch (error) {
    // Silently fail if event listeners fail
  }
})();